import { create } from 'zustand';
import { useApiStore } from './api';
import { createApiClient, setAuthFailureCallback } from '../lib/api-client';
import {
  buildWorkOSAuthUrl,
  generateCodeChallenge,
  generateCodeVerifier,
  generateState,
} from '../lib/pkce';
import { clearTokens, hasAuthSession, setAuthSessionActive } from '../lib/token-storage';

const CODE_VERIFIER_KEY = 'qc_code_verifier';
const AUTH_STATE_KEY = 'qc_auth_state';

interface AuthState {
  user: { sub: string; username: string; user_type: string } | null;
  isAuthenticated: boolean;
  isLoading: boolean;

  // Actions
  startAuthFlow: () => Promise<void>;
  handleCallback: (code: string, state: string) => Promise<void>;
  logout: () => Promise<void>;
  getUserInfo: () => Promise<void>;
  checkAuth: () => Promise<boolean>;
  handleAuthFailure: () => void;
}

/**
 * Auth store using WorkOS AuthKit with PKCE OAuth flow.
 *
 * Tokens (access + refresh) are stored in localStorage under `qc_*` keys.
 * The API client reads the access token and injects it as Authorization: Bearer.
 *
 * Flow:
 *   1. startAuthFlow() — generate PKCE params, redirect to WorkOS
 *   2. WorkOS redirects back to {basename}/auth/callback with code + state
 *   3. handleCallback() — exchange code for tokens, fetch user info
 */
export const useAuthStore = create<AuthState>()((set, get) => ({
  user: null,
  isAuthenticated: false,
  isLoading: true,

  startAuthFlow: async () => {
    const clientId = import.meta.env.VITE_WORKOS_CLIENT_ID as string | undefined;
    if (!clientId) {
      throw new Error('WorkOS client ID is not configured (VITE_WORKOS_CLIENT_ID)');
    }

    const verifier = generateCodeVerifier();
    const challenge = await generateCodeChallenge(verifier);
    const state = generateState();

    sessionStorage.setItem(CODE_VERIFIER_KEY, verifier);
    sessionStorage.setItem(AUTH_STATE_KEY, state);

    const basename = import.meta.env.VITE_APP_BASENAME || '';
    const redirectUri = `${window.location.origin}${basename}/auth/callback`;
    const authUrl = buildWorkOSAuthUrl(clientId, redirectUri, challenge, state);

    window.location.href = authUrl;
  },

  handleCallback: async (code: string, state: string) => {
    const expectedState = sessionStorage.getItem(AUTH_STATE_KEY);
    if (!expectedState || expectedState !== state) {
      throw new Error('Invalid state parameter — possible CSRF attack');
    }

    const verifier = sessionStorage.getItem(CODE_VERIFIER_KEY);
    if (!verifier) {
      throw new Error('Missing code verifier — please restart the sign-in flow');
    }

    const apiUrl = useApiStore.getState().apiUrl;

    // Use raw fetch with credentials: 'include' so the browser accepts
    // HttpOnly Set-Cookie headers from the response.
    const response = await fetch(`${apiUrl}/v1/auth/callback`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ code, code_verifier: verifier }),
    });

    if (!response.ok) {
      const body = (await response.json().catch(() => ({}))) as { error?: string };
      throw new Error(body.error || 'Authentication failed');
    }

    // Tokens are set as HttpOnly cookies by the backend response.
    // We just need to mark the session as active locally.
    await response.json(); // consume body
    setAuthSessionActive();

    // Clean up PKCE session state
    sessionStorage.removeItem(CODE_VERIFIER_KEY);
    sessionStorage.removeItem(AUTH_STATE_KEY);

    // Populate user state
    await get().getUserInfo();
  },

  logout: async () => {
    const apiUrl = useApiStore.getState().apiUrl;
    let logoutUrl: string | null = null;

    try {
      const client = createApiClient(apiUrl);
      const { data } = await client.POST('/v1/auth/logout', {
        body: {},
      });
      logoutUrl = (data as { logout_url?: string } | undefined)?.logout_url ?? null;
    } catch (error) {
      console.error('Logout API call failed:', error);
    }

    clearTokens();
    set({
      user: null,
      isAuthenticated: false,
    });

    // Redirect to WorkOS logout to revoke the IdP session.
    // Without this, clicking "Sign In" would silently re-authenticate.
    if (logoutUrl) {
      window.location.href = logoutUrl;
    }
  },

  getUserInfo: async () => {
    const apiUrl = useApiStore.getState().apiUrl;
    const client = createApiClient(apiUrl);

    const { data, error } = await client.GET('/v1/auth/userinfo');

    if (error || !data) {
      throw new Error('Failed to fetch user info');
    }

    set({
      user: data,
      isAuthenticated: true,
      isLoading: false,
    });
  },

  checkAuth: async () => {
    // With HttpOnly cookies, we can't check the token directly.
    // If no session flag is set, skip the network call.
    if (!hasAuthSession()) {
      set({ user: null, isAuthenticated: false, isLoading: false });
      return false;
    }

    const apiUrl = useApiStore.getState().apiUrl;
    const client = createApiClient(apiUrl);

    try {
      const { data, error } = await client.GET('/v1/auth/userinfo');

      if (error || !data) {
        set({ user: null, isAuthenticated: false, isLoading: false });
        return false;
      }

      set({
        user: data,
        isAuthenticated: true,
        isLoading: false,
      });
      return true;
    } catch {
      set({ user: null, isAuthenticated: false, isLoading: false });
      return false;
    }
  },

  handleAuthFailure: () => {
    console.warn('[Auth] Session expired, redirecting to login');
    clearTokens();
    set({
      user: null,
      isAuthenticated: false,
      isLoading: false,
    });
  },
}));

// Register the auth failure callback with the API client
// This is called once when the module loads
setAuthFailureCallback(() => {
  useAuthStore.getState().handleAuthFailure();
});
